import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Tooltip
} from '@mui/material';
import StyledDataGrid from 'components/atoms/StyledDataGrid';
import { formatDateTime } from 'utils/dateTime';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * CopyOrdersModal Component
 * 
 * Displays a modal with a table of copy orders for a selected main order.
 * Highlights copy orders with status different from the main order.
 */
const CopyOrdersModal = ({ 
  open, 
  onClose, 
  mainOrder, 
  copyOrders, 
  loading 
}) => {
  // Get the main order status for comparison
  const mainOrderStatus = mainOrder?.order_status?.[0]?.order_status || '';
  
  // Define columns for the copy orders DataGrid
  const copyOrderColumns = [
    { field: 'id', headerName: 'Order ID', width: 150 },
    { 
      field: 'user', 
      headerName: 'User', 
      width: 180,
      valueGetter: (params) => params.value?.username || 'N/A' 
    },
    {
      field: 'subscription',
      headerName: 'Subscription',
      width: 150,
      valueGetter: (params) => params.value?.id || 'N/A'
    },
    {
      field: 'data',
      headerName: 'Symbol',
      width: 100,
      valueGetter: (params) => params.value?.symbol || 'N/A'
    },
    {
      field: 'dataType',
      headerName: 'Type',
      width: 100,
      valueGetter: (params) => params.row.data?.order_type || 'N/A'
    },
    {
      field: 'dataSide',
      headerName: 'Side',
      width: 80,
      valueGetter: (params) => params.row.data?.side || 'N/A'
    },
    {
      field: 'dataQuantity',
      headerName: 'Quantity',
      width: 100,
      valueGetter: (params) => params.row.data?.quantity || 'N/A'
    },
    {
      field: 'dataPrice',
      headerName: 'Price',
      width: 100,
      valueGetter: (params) => {
        const data = params.row.data || {};
        return data.price || data.limit_price || data.stop_price || 'N/A';
      }
    },
    { 
      field: 'created', 
      headerName: 'Date/Time', 
      width: 150,
      valueFormatter: (params) => formatDateTime(params.value)
    },
    {
      field: 'orderStatus',
      headerName: 'Status',
      width: 120,
      valueGetter: (params) => {
        if (!params.row.order_status || !params.row.order_status.length) return 'N/A';
        return params.row.order_status[0]?.order_status || 'N/A';
      },
      renderCell: (params) => {
        const status = params.value;
        let color = 'text.primary';
        
        if (status === 'Filled') color = 'success.dark';
        else if (status === 'Submitted') color = 'info.main';
        else if (status === 'Cancelled') color = 'error.main';
        else if (status === 'PendingCancel') color = 'warning.main';
        
        // Highlight if different from main order
        const isMismatch = status !== mainOrderStatus;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color={color} fontWeight={isMismatch ? 'bold' : 'normal'}>
              {status}
            </Typography>
            {isMismatch && (
              <Tooltip title="Different from main order">
                <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Copy Orders for Order {mainOrder?.id}
        </Typography>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Main Order Status: 
            <Box component="span" sx={{ ml: 1, fontWeight: 'bold' }}>
              {mainOrderStatus || 'N/A'}
            </Box>
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {copyOrders?.length || 0} copy orders. Orders highlighted in yellow have status different from the main order.
              </Typography>
            </Box>
            
            <StyledDataGrid
              rows={copyOrders || []}
              columns={copyOrderColumns}
              autoHeight
              disableSelectionOnClick
              disableRowSelectionOnClick
              pageSize={5}
              rowsPerPageOptions={[5, 10, 25]}
              getRowClassName={(params) => {
                const copyStatus = params.row.order_status?.[0]?.order_status || '';
                return copyStatus !== mainOrderStatus ? 'status-mismatch-row' : '';
              }}
              initialState={{
                density: 'compact',
              }}
              sx={{
                '.status-mismatch-row': {
                  backgroundColor: 'warning.light',
                }
              }}
            />
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyOrdersModal; 